import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { usePageAuthorization } from '../hooks/usePageAuthorization';

// Task 1.1: VenueEvent interface with full event fields
interface VenueEvent {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
}

interface VenueItem {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  capacity: number;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  parkingInfo?: string | null;
  parkingImageUrl?: string | null;
  events?: VenueEvent[];
  _count?: { events: number };
}

interface VenueFormData {
  name: string;
  address: string;
  city: string;
  state: string;
  capacity: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  parkingInfo: string;
  parkingImageUrl: string;
}

const emptyForm: VenueFormData = {
  name: '',
  address: '',
  city: '',
  state: '',
  capacity: '',
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  parkingInfo: '',
  parkingImageUrl: '',
};

// Task 2.2: Date/time formatting helpers
function formatEventDate(isoDate: string): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const datePart = isoDate.split('T')[0];
  const [year, month, day] = datePart.split('-');
  const monthIndex = parseInt(month, 10) - 1;
  const dayNum = parseInt(day, 10);
  return `${months[monthIndex]} ${dayNum}, ${year}`;
}

function formatEventTime(isoDateTime: string): string {
  const timePart = isoDateTime.split('T')[1];
  if (!timePart) return '';
  const [hourStr, minuteStr] = timePart.split(':');
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  if (hour === 0) hour = 12;
  else if (hour > 12) hour -= 12;
  return `${hour}:${minute} ${ampm}`;
}

// Task 2.1: VenueDetailPanel component
function VenueDetailPanel({ venue, onPrint }: { venue: VenueItem; onPrint: () => void }) {
  return (
    <div style={detailStyles.panel}>
      <div style={detailStyles.header}>
        <h3 style={detailStyles.venueName}>{venue.name}</h3>
        <button
          style={detailStyles.printBtn}
          onClick={(e) => { e.stopPropagation(); onPrint(); }}
        >
          🖨️ Print
        </button>
      </div>

      <div style={detailStyles.infoGrid}>
        <div><strong>Address:</strong> {venue.address}</div>
        <div><strong>City:</strong> {venue.city}</div>
        <div><strong>State:</strong> {venue.state}</div>
        <div><strong>Capacity:</strong> {venue.capacity.toLocaleString()}</div>
      </div>

      <h4 style={detailStyles.eventsTitle}>Scheduled Events</h4>
      {venue.events && venue.events.length > 0 ? (
        <table style={detailStyles.table}>
          <thead>
            <tr>
              <th style={detailStyles.th}>Event Name</th>
              <th style={detailStyles.th}>Date</th>
              <th style={detailStyles.th}>Start</th>
              <th style={detailStyles.th}>End</th>
              <th style={detailStyles.th}>Capacity</th>
            </tr>
          </thead>
          <tbody>
            {venue.events.map((event) => (
              <tr key={event.id}>
                <td style={detailStyles.td}>{event.name}</td>
                <td style={detailStyles.td}>{formatEventDate(event.date)}</td>
                <td style={detailStyles.td}>{formatEventTime(event.startTime)}</td>
                <td style={detailStyles.td}>{formatEventTime(event.endTime)}</td>
                <td style={detailStyles.td}>{event.maxCapacity.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={detailStyles.noEvents}>No events scheduled at this venue.</p>
      )}
    </div>
  );
}

// Task 5.1: PrintableVenueDetail component
function PrintableVenueDetail({ venue }: { venue: VenueItem }) {
  return (
    <div id="printable-venue-detail" style={{ padding: '2rem' }}>
      <h2 style={{ color: '#1a6b3c', marginBottom: '0.5rem' }}>{venue.name}</h2>
      <p style={{ margin: '0.25rem 0' }}><strong>Address:</strong> {venue.address}</p>
      <p style={{ margin: '0.25rem 0' }}><strong>City:</strong> {venue.city}</p>
      <p style={{ margin: '0.25rem 0' }}><strong>State:</strong> {venue.state}</p>
      <p style={{ margin: '0.25rem 0' }}><strong>Capacity:</strong> {venue.capacity.toLocaleString()}</p>

      <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}>Scheduled Events</h3>
      {venue.events && venue.events.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Event Name</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Date</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Start</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>End</th>
              <th style={{ borderBottom: '2px solid #333', padding: '0.5rem', textAlign: 'left' }}>Capacity</th>
            </tr>
          </thead>
          <tbody>
            {venue.events.map((event) => (
              <tr key={event.id}>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{event.name}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{formatEventDate(event.date)}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{formatEventTime(event.startTime)}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{formatEventTime(event.endTime)}</td>
                <td style={{ borderBottom: '1px solid #ddd', padding: '0.5rem' }}>{event.maxCapacity.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>No events scheduled at this venue.</p>
      )}
    </div>
  );
}

const detailStyles: Record<string, React.CSSProperties> = {
  panel: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderTop: 'none',
    borderRadius: '0 0 6px 6px',
    padding: '1.25rem',
    marginBottom: '0.75rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  venueName: {
    color: '#1a6b3c',
    fontSize: '1.25rem',
    margin: 0,
  },
  printBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
    marginBottom: '1.25rem',
    fontSize: '0.9rem',
    color: '#333',
  },
  eventsTitle: {
    color: '#0f2d1f',
    fontSize: '1rem',
    marginBottom: '0.75rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.5rem',
    borderBottom: '2px solid #e0e0e0',
    color: '#555',
    fontWeight: 600,
  },
  td: {
    padding: '0.5rem',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
  },
  noEvents: {
    color: '#888',
    fontStyle: 'italic' as const,
    fontSize: '0.9rem',
  },
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: 0,
  },
  addButton: {
    padding: '0.6rem 1.2rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  venueCard: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  venueHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  venueName: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#0f2d1f',
    marginBottom: '0.25rem',
  },
  venueMeta: {
    fontSize: '0.85rem',
    color: '#555',
    marginBottom: '0.25rem',
  },
  badge: {
    display: 'inline-block',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    padding: '0.2rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  editBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#c9a84c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  deleteBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '2rem',
    width: '100%',
    maxWidth: '680px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
  },
  modalTitle: {
    color: '#1a6b3c',
    marginBottom: '1.25rem',
    fontSize: '1.25rem',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box' as const,
  },
  submitBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  cancelBtn: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  warning: {
    color: '#e65100',
    backgroundColor: '#fff3e0',
    padding: '0.75rem',
    borderRadius: '4px',
    marginTop: '1rem',
    fontSize: '0.85rem',
  },
};

export function VenuesPage() {
  const { canEdit } = usePageAuthorization('venues');
  const [venues, setVenues] = useState<VenueItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VenueFormData>(emptyForm);
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);

  // Task 1.1: Expansion and print state
  const [expandedVenueId, setExpandedVenueId] = useState<string | null>(null);
  const [printingVenueId, setPrintingVenueId] = useState<string | null>(null);

  // Task 1.1: Toggle expansion function
  function toggleVenueExpansion(venueId: string) {
    setExpandedVenueId((prev) => (prev === venueId ? null : venueId));
  }

  // Task 5.2: handlePrint function
  function handlePrint(venueId: string) {
    setPrintingVenueId(venueId);
    setTimeout(() => {
      window.print();
    }, 100);
  }

  // Task 5.2: afterprint event listener
  useEffect(() => {
    const afterPrint = () => setPrintingVenueId(null);
    window.addEventListener('afterprint', afterPrint);
    return () => window.removeEventListener('afterprint', afterPrint);
  }, []);

  async function fetchVenues() {
    try {
      const data = await apiClient<VenueItem[]>('/venues');
      setVenues(data);
    } catch {
      setVenues([]);
    }
  }

  useEffect(() => {
    fetchVenues();
  }, []);

  function openCreate() {
    setForm(emptyForm);
    setEditingId(null);
    setFormError('');
    setShowModal(true);
  }

  function openEdit(venue: VenueItem) {
    setForm({
      name: venue.name,
      address: venue.address,
      city: venue.city,
      state: venue.state,
      capacity: String(venue.capacity),
      contactName: venue.contactName || '',
      contactPhone: venue.contactPhone || '',
      contactEmail: venue.contactEmail || '',
      parkingInfo: venue.parkingInfo || '',
      parkingImageUrl: venue.parkingImageUrl || '',
    });
    setEditingId(venue.id);
    setFormError('');
    setShowModal(true);
  }

  async function handleDelete(venue: VenueItem) {
    const eventCount = venue._count?.events || venue.events?.length || 0;

    if (eventCount > 0) {
      const eventNames = venue.events?.map((e) => e.name).join(', ') || `${eventCount} event(s)`;
      alert(
        `Cannot delete "${venue.name}" because it has associated events: ${eventNames}.\n\nPlease reassign or cancel those events first.`,
      );
      return;
    }

    if (!confirm(`Are you sure you want to delete "${venue.name}"?`)) {
      return;
    }

    try {
      await apiClient(`/venues/${venue.id}`, { method: 'DELETE' });
      fetchVenues();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      alert(apiError.message || 'Failed to delete venue.');
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!form.name || !form.address || !form.city || !form.state || !form.capacity) {
      setFormError('All fields are required.');
      return;
    }

    const capacity = parseInt(form.capacity, 10);
    if (isNaN(capacity) || capacity < 1 || capacity > 100000) {
      setFormError('Capacity must be between 1 and 100,000.');
      return;
    }

    setLoading(true);

    const payload = {
      name: form.name,
      address: form.address,
      city: form.city,
      state: form.state,
      capacity,
      contactName: form.contactName.trim() || undefined,
      contactPhone: form.contactPhone.trim() || undefined,
      contactEmail: form.contactEmail.trim() || undefined,
      parkingInfo: form.parkingInfo.trim() || undefined,
      parkingImageUrl: form.parkingImageUrl.trim() || undefined,
    };

    try {
      if (editingId) {
        await apiClient(`/venues/${editingId}`, { method: 'PATCH', body: payload });
      } else {
        await apiClient('/venues', { method: 'POST', body: payload });
      }
      setShowModal(false);
      fetchVenues();
    } catch (err: unknown) {
      const apiError = err as { message?: string };
      setFormError(apiError.message || 'Failed to save venue.');
    } finally {
      setLoading(false);
    }
  }

  const printingVenue = printingVenueId ? venues.find((v) => v.id === printingVenueId) : null;

  return (
    <div style={styles.page}>
      {/* Task 5.1: Print CSS media query */}
      {printingVenueId && (
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #printable-venue-detail, #printable-venue-detail * {
              visibility: visible;
            }
            #printable-venue-detail {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
        `}</style>
      )}

      <div style={styles.header}>
        <h1 style={styles.title}>Venues</h1>
        {canEdit && <button style={styles.addButton} onClick={openCreate}>+ Add Venue</button>}
      </div>

      {venues.length === 0 && (
        <p style={{ color: '#666' }}>No venues yet. Click "Add Venue" to create one.</p>
      )}

      {venues.map((venue) => {
        const eventCount = venue._count?.events || venue.events?.length || 0;
        const isExpanded = expandedVenueId === venue.id;
        return (
          <div key={venue.id} style={{ marginBottom: '0.75rem' }}>
            {/* Task 3.1: Click handler on venue card */}
            <div
              style={{
                ...styles.venueCard,
                ...(isExpanded ? { borderRadius: '6px 6px 0 0', marginBottom: 0 } : {}),
              }}
              onClick={() => toggleVenueExpansion(venue.id)}
            >
              <div style={styles.venueHeader}>
                <div>
                  <div style={styles.venueName}>{venue.name}</div>
                  <div style={styles.venueMeta}>
                    {venue.address}, {venue.city}, {venue.state}
                  </div>
                  <div style={styles.venueMeta}>
                    Capacity: {venue.capacity.toLocaleString()}
                    <span style={{ ...styles.badge, marginLeft: '0.75rem' }}>
                      {eventCount} event{eventCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div style={styles.actions}>
                  {/* Task 3.1: stopPropagation on Edit/Delete */}
                  {canEdit && <button style={styles.editBtn} onClick={(e) => { e.stopPropagation(); openEdit(venue); }}>Edit</button>}
                  {canEdit && <button style={styles.deleteBtn} onClick={(e) => { e.stopPropagation(); handleDelete(venue); }}>Delete</button>}
                </div>
              </div>
              {/* Task 5.1: Contact info section */}
              {(venue.contactName || venue.contactPhone || venue.contactEmail || venue.parkingInfo) && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6 }}>
                  {(venue.contactName || venue.contactPhone) && (
                    <div>
                      📞 {venue.contactName ? `Contact: ${venue.contactName}` : ''}
                      {venue.contactName && venue.contactPhone ? ' · ' : ''}
                      {venue.contactPhone && !venue.contactName ? `Contact: ${venue.contactPhone}` : ''}
                      {venue.contactPhone && venue.contactName ? venue.contactPhone : ''}
                    </div>
                  )}
                  {venue.contactEmail && (
                    <div>
                      ✉️ <a href={`mailto:${venue.contactEmail}`} style={{ color: '#1a6b3c', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>{venue.contactEmail}</a>
                    </div>
                  )}
                  {venue.parkingInfo && (
                    <div>🅿️ Parking: {venue.parkingInfo}</div>
                  )}
                </div>
              )}
            </div>
            {/* Task 3.1: Conditionally render detail panel */}
            {isExpanded && (
              <VenueDetailPanel venue={venue} onPrint={() => handlePrint(venue.id)} />
            )}
          </div>
        );
      })}

      {/* Task 5.1: Printable component rendered when printing */}
      {printingVenue && <PrintableVenueDetail venue={printingVenue} />}

      {/* Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>{editingId ? 'Edit Venue' : 'Add Venue'}</h2>

            {formError && <div style={styles.error}>{formError}</div>}

            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueName">Name</label>
                <input
                  id="venueName"
                  style={styles.input}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueAddress">Street Address</label>
                <input
                  id="venueAddress"
                  style={styles.input}
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="venueCity">City</label>
                  <input
                    id="venueCity"
                    style={styles.input}
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                </div>
                <div style={{ ...styles.field, flex: 1 }}>
                  <label style={styles.label} htmlFor="venueState">State</label>
                  <input
                    id="venueState"
                    style={styles.input}
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueCapacity">Capacity</label>
                <input
                  id="venueCapacity"
                  type="number"
                  min="1"
                  max="100000"
                  style={styles.input}
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                />
              </div>

              {/* Venue Contact Section */}
              <h3 style={{ color: '#1a6b3c', fontSize: '1rem', margin: '1.25rem 0 0.75rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
                Venue Contact
              </h3>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueContactName">Contact Name</label>
                <input
                  id="venueContactName"
                  style={styles.input}
                  maxLength={200}
                  placeholder="Contact person name"
                  value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueContactPhone">Contact Phone</label>
                <input
                  id="venueContactPhone"
                  style={styles.input}
                  maxLength={20}
                  placeholder="Phone number"
                  value={form.contactPhone}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueContactEmail">Contact Email</label>
                <input
                  id="venueContactEmail"
                  type="email"
                  style={styles.input}
                  maxLength={200}
                  placeholder="Email address"
                  value={form.contactEmail}
                  onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueParkingInfo">Parking Info</label>
                <textarea
                  id="venueParkingInfo"
                  style={{ ...styles.input, resize: 'vertical' as const }}
                  rows={3}
                  placeholder="Parking details..."
                  value={form.parkingInfo}
                  onChange={(e) => setForm({ ...form, parkingInfo: e.target.value })}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label} htmlFor="venueParkingImage">Parking Map Image</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    id="venueParkingImage"
                    type="text"
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="Image URL or upload below..."
                    value={form.parkingImageUrl}
                    onChange={(e) => setForm({ ...form, parkingImageUrl: e.target.value })}
                  />
                </div>
                <input
                  type="file"
                  accept="image/*"
                  style={{ marginTop: '6px', fontSize: '0.8rem' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      if (typeof reader.result === 'string') {
                        setForm((prev) => ({ ...prev, parkingImageUrl: reader.result as string }));
                      }
                    };
                    reader.readAsDataURL(file);
                  }}
                />
                {form.parkingImageUrl && (
                  <div style={{ marginTop: '8px' }}>
                    <img src={form.parkingImageUrl} alt="Parking map preview" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '6px', border: '1px solid #e0e0e0' }} />
                    <button type="button" onClick={() => setForm({ ...form, parkingImageUrl: '' })} style={{ display: 'block', marginTop: '4px', background: 'none', border: 'none', color: '#d32f2f', fontSize: '0.75rem', cursor: 'pointer' }}>✕ Remove image</button>
                  </div>
                )}
              </div>

              <button type="submit" style={styles.submitBtn} disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update Venue' : 'Create Venue'}
              </button>
              <button type="button" style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
